import styled, { css } from 'styled-components';

/**
 * Creates a wavy edge/border.
 *
 * To use, place the edge in a container that has the style: `position: relative`.
 *
 * Args
 * position: Which edge of the element will be wavy
 * outside: Whether a solid background encroaches on the element, or a solid color element encroaches on a background
 * amplitude: The height of each wave
 * frequency: The period of the wave
 * color: The desired solid color of the wavy edge
 */

// Frequency & Amplitude
export const DEFAULT_FREQUENCY = 16;
export const DEFAULT_AMPLITUDE = 16;
export const DEFAULT_COLOR = '#FFFFFF';

export type Position = 'top' | 'bottom' | 'left' | 'right';

export const WavyEdge = styled.div<{
  position?: Position;
  outside?: boolean;
  amplitude?: number;
  frequency?: number;
  color?: string;
}>`
  ${({ position, outside, amplitude: _amplitude, frequency: _frequency, color }) => {
    const amplitude = _amplitude ?? DEFAULT_AMPLITUDE;
    const frequency = _frequency ?? DEFAULT_FREQUENCY;

    switch (position) {
      case 'top':
        return css`
          height: ${amplitude}px;
          width: 100%;

          position: absolute;
          ${!outside
            ? css`
                top: -${amplitude / 4}px;
              `
            : css`
                top: -${amplitude * (3 / 4)}px;
                transform: scale(1, -1);
              `}
          margin-top: 0;

          overflow: hidden;

          &:before,
          &:after {
            content: '';
            position: absolute;
            left: -${frequency}px;
            right: 0;
            height: ${amplitude}px;
            background-size: ${frequency * 2}px ${amplitude}px !important;
            background-repeat: repeat-x !important;
          }

          &:before {
            background: radial-gradient(
              closest-side,
              ${color ?? DEFAULT_COLOR},
              ${color ?? DEFAULT_COLOR} 55%,
              transparent 55%
            );
            background-position: 0 -${amplitude / 16}px;
            top: 0;
          }

          &:after {
            background: radial-gradient(closest-side, transparent, transparent 55%, ${color ?? DEFAULT_COLOR} 55%);
            background-position: ${frequency}px ${amplitude * (5 / 8)}px;
            top: -${amplitude * (7 / 16)}px;
          }
        `;

      case 'left':
        return css`
          width: ${amplitude}px;
          height: 100%;

          position: absolute;
          top: 0;
          ${!outside
            ? css`
                left: -${amplitude / 4}px;
              `
            : css`
                left: -${amplitude * (3 / 4)}px;
                transform: scale(-1, 1);
              `}
          margin-left: 0;

          overflow: hidden;

          &:before,
          &:after {
            content: '';
            position: absolute;
            top: -${frequency}px;
            bottom: 0;
            width: ${amplitude}px;
            background-size: ${amplitude}px ${frequency * 2}px !important;
            background-repeat: repeat-y !important;
          }

          // Top
          &:before {
            background: radial-gradient(
              closest-side,
              ${color ?? DEFAULT_COLOR},
              ${color ?? DEFAULT_COLOR} 55%,
              transparent 55%
            );
            background-position: -${amplitude / 16}px 0;
            left: 0;
          }

          // Bottom
          &:after {
            background: radial-gradient(closest-side, transparent, transparent 55%, ${color ?? DEFAULT_COLOR} 55%);
            background-position: ${amplitude * (5 / 8)}px ${frequency}px;
            left: -${amplitude * (7 / 16)}px;
          }
        `;

      case 'right':
        return css`
          width: ${amplitude}px;
          height: 100%;

          position: absolute;
          top: 0;
          ${!outside
            ? css`
                right: -${amplitude / 4}px;
              `
            : css`
                right: -${amplitude * (3 / 4)}px;
                transform: scale(-1, 1);
              `}
          margin-right: 0;

          overflow: hidden;

          &:before,
          &:after {
            content: '';
            position: absolute;
            top: -${frequency}px;
            bottom: 0;
            width: ${amplitude}px;
            background-size: ${amplitude}px ${frequency * 2}px !important;
            background-repeat: repeat-y !important;
          }

          &:before {
            background: radial-gradient(
              closest-side,
              ${color ?? DEFAULT_COLOR},
              ${color ?? DEFAULT_COLOR} 55%,
              transparent 55%
            );
            background-position: ${amplitude / 16}px 0;
            right: 0;
          }

          &:after {
            background: radial-gradient(closest-side, transparent, transparent 55%, ${color ?? DEFAULT_COLOR} 55%);
            background-position: -${amplitude * (5 / 8)}px ${frequency}px;
            right: -${amplitude * (7 / 16)}px;
          }
        `;

      case 'bottom':
      default:
        return css`
          height: ${amplitude}px;
          width: 100%;

          position: absolute;
          ${!outside
            ? css`
                bottom: -${amplitude / 4}px;
              `
            : css`
                bottom: -${amplitude * (3 / 4)}px;
                transform: scale(1, -1);
              `}
          margin-bottom: 0;

          overflow: hidden;

          &:before,
          &:after {
            content: '';
            position: absolute;
            left: -${frequency}px;
            right: 0;
            height: ${amplitude}px;
            background-size: ${frequency * 2}px ${amplitude}px !important;
            background-repeat: repeat-x !important;
          }

          &:before {
            background: radial-gradient(
              closest-side,
              ${color ?? DEFAULT_COLOR},
              ${color ?? DEFAULT_COLOR} 55%,
              transparent 55%
            );
            background-position: 0 ${amplitude / 16}px;
            bottom: 0;
          }

          &:after {
            background: radial-gradient(closest-side, transparent, transparent 55%, ${color ?? DEFAULT_COLOR} 55%);
            background-position: ${frequency}px -${amplitude * (5 / 8)}px;
            bottom: -${amplitude * (7 / 16)}px;
          }
        `;
    }
  }};
`;
